/**
 * TypeScript client for JaPlanchers.ca external conversations API
 * Fetches conversation threads with customer info and message history
 */

export interface JaPlanchersCustomer {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string | null;
  phone: string | null;
}

export interface JaPlanchersMessage {
  id: number;
  direction: string; // 'inbound' | 'outbound'
  from_phone: string | null;
  to_phone: string | null;
  body: string;
  status: string;
  twilio_sid: string | null;
  sent_at: string | null; // ISO8601 timestamp
  created_at: string | null;
}

export interface JaPlanchersConversation {
  id: number;
  status: string;
  last_message_at: string | null;
  message_count: number;
  created_at: string | null;
  customer: JaPlanchersCustomer;
  messages: JaPlanchersMessage[];
}

interface JaPlanchersResponse {
  success: boolean;
  count: number;
  data: JaPlanchersConversation[];
}

export class JaPlanchersAPIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public responseBody?: string
  ) {
    super(message);
    this.name = 'JaPlanchersAPIError';
  }
}

export class JaPlanchersAPI {
  private static readonly BASE_URL = 'https://japlanchers.ca';
  private apiKey: string;

  constructor(apiKey: string) {
    if (!apiKey || apiKey.trim().length === 0) {
      throw new Error('API key is required');
    }
    this.apiKey = apiKey;
  }

  /**
   * Fetch recent conversations from JaPlanchers API
   * @param limit Maximum number of conversations to fetch (1-50)
   * @returns Array of conversation objects with customer and messages
   */
  async fetchRecentConversations(limit: number = 50): Promise<JaPlanchersConversation[]> {
    // Validate limit
    if (limit < 1 || limit > 50) {
      throw new Error('Limit must be between 1 and 50');
    }

    // Build URL with query params
    const url = new URL('/api/conversations/recent', JaPlanchersAPI.BASE_URL);
    url.searchParams.set('limit', String(limit));

    console.log(`[JaPlanchersAPI] Fetching ${limit} recent conversations...`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'X-API-Key': this.apiKey,
          'Accept': 'application/json',
        },
      });

      // Handle non-200 responses
      if (!response.ok) {
        const body = await response.text();
        throw new JaPlanchersAPIError(
          `API request failed: ${response.status} ${response.statusText}`,
          response.status,
          body
        );
      }

      // Parse JSON response
      const json: JaPlanchersResponse = await response.json();

      if (!json.success) {
        throw new JaPlanchersAPIError('API returned success: false');
      }

      console.log(`[JaPlanchersAPI] Fetched ${json.count} conversations`);
      return json.data;

    } catch (error) {
      if (error instanceof JaPlanchersAPIError) {
        throw error;
      }

      // Network or parsing errors
      if (error instanceof TypeError) {
        throw new JaPlanchersAPIError('Network error: Failed to connect to API');
      }

      throw new JaPlanchersAPIError(
        `Unexpected error: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Test API connection and authentication
   * @returns True if API key is valid and connection works
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.fetchRecentConversations(1);
      return true;
    } catch (error) {
      console.error('[JaPlanchersAPI] Connection test failed:', error);
      return false;
    }
  }
}
